// src/pages/CustomersList.js
import React, { useState, useEffect, useContext } from 'react';
import { Typography, Paper, Button, Box, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Checkbox } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TanStackTable from '../components/TanStackTable';
import { getCustomers, deleteMultipleCustomers } from '../services/customerService';
import { OrgContext } from '../contexts/OrgContext'; // Import OrgContext
import Papa from 'papaparse';
import BulkActionResults from '../components/BulkActionResults';

const CustomersList = () => {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { orgId } = useContext(OrgContext); // Access orgId from OrgContext
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [deleteResults, setDeleteResults] = useState(null);
  
  // Pagination and filtering state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('customer_name');
  const [sortOrder, setSortOrder] = useState('ASC');

  const fetchCustomers = React.useCallback(async (customOptions = {}) => {
    if (!orgId) return;
    
    setLoading(true);
    try {
      const requestOptions = {
        page: customOptions.page !== undefined ? customOptions.page : pagination.page,
        limit: customOptions.limit !== undefined ? customOptions.limit : pagination.limit,
        search: customOptions.search !== undefined ? customOptions.search : searchQuery,
        sortBy: customOptions.sortBy !== undefined ? customOptions.sortBy : sortBy,
        sortOrder: customOptions.sortOrder !== undefined ? customOptions.sortOrder : sortOrder
      };

      const response = await getCustomers(orgId, requestOptions);
      
      console.log('Customers API Response:', response);
      console.log('Request Options:', requestOptions);
      
      // Check if response is paginated
      if (response.data && Array.isArray(response.data)) {
        console.log('Setting paginated data:', response.data, 'Pagination:', response.pagination);
        setRowData(response.data);
        setPagination(response.pagination);
      } else if (Array.isArray(response)) {
        // Fallback for non-paginated response
        console.log('Setting non-paginated data:', response);
        setRowData(response);
      }
    } finally {
      setLoading(false);
    }
  }, [orgId, pagination.page, pagination.limit, searchQuery, sortBy, sortOrder]);

  // Initial load - only run when orgId changes
  useEffect(() => {
    if (orgId) {
      fetchCustomers();
    }
  }, [orgId]); // Remove fetchCustomers dependency to prevent re-render loops

  const columns = [
    {
      accessorKey: 'id',
      header: 'ID',
    },
    {
      accessorKey: 'customer_name',
      header: 'Name',
    },
    {
      accessorKey: 'email',
      header: 'Email',
    },
    {
      accessorKey: 'contact_number',
      header: 'Phone',
    },
    {
      accessorKey: 'whatsapp_number',
      header: 'Whatsapp Number',
      accessorFn: (row) => row.meta?.whatsapp_number || '',
    },
  ];

  const handleRowClick = (row) => {
    navigate(`/${orgId}/customers/${row.id}`);
  };

  const handleDownload = () => {
    const exportData = rowData.map(customer => ({
      'Customer ID': customer.id,
      'Name': customer.customer_name,
      'Email': customer.email,
      'Phone': customer.contact_number,
      'Whatsapp Number': customer.meta?.whatsapp_number || '',
      'Address': customer.address,
      'City': customer.city,
      'State': customer.state,
      'Country': customer.country,
      'Postal Code': customer.postal_code
    }));

    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const now = new Date();
    const isoDate = now.toISOString().slice(0, 10); // yyyy-mm-dd format
    const uniqueId = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}${String(now.getMilliseconds()).padStart(3, '0')}`;
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${isoDate}_customers_${uniqueId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteClick = () => {
    setDeleteMode(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await deleteMultipleCustomers(selectedCustomers);
      setDeleteResults(response);
      await fetchCustomers();
    } catch (error) {
      console.error('Error deleting customers:', error);
    } finally {
      setDeleteMode(false);
      setSelectedCustomers([]);
    }
  };

  return (
    <Paper sx={{
      p: 2,
      bgcolor: 'background.paper',
      color: 'text.primary',
      backdropFilter: 'saturate(140%) blur(6px)',
      border: '1px solid',
      borderColor: 'divider',
    }}>
      <Typography variant="h5" gutterBottom>
        Customers
      </Typography>
      {deleteResults && (
        <BulkActionResults 
          results={deleteResults} 
          actionType="deletion"
          onClose={() => setDeleteResults(null)}
        />
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        {!deleteMode ? (
          <>
            <Button variant="contained" onClick={() => navigate(`/${orgId}/customers/add`)}>
              Add Customer
            </Button>
            <Button variant="contained" onClick={() => navigate(`/${orgId}/customers/bulk-import`)}>
              Add Customers in Bulk
            </Button>
            <Button variant="contained" onClick={handleDownload}>
              Download Customers List
            </Button>
            <Button 
              variant="contained" 
              onClick={() => navigate(`/${orgId}/accounts-receivable`)}
            >
              View Accounts Receivable
            </Button>
            <Button variant="contained" color="error" onClick={handleDeleteClick}>
              Delete Customers
            </Button>
          </>
        ) : (
          <>
            <Button variant="contained" color="error" onClick={handleConfirmDelete}>
              Confirm Delete
            </Button>
            <Button variant="contained" onClick={() => setDeleteMode(false)}>
              Cancel
            </Button>
          </>
        )}
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {!deleteMode && (
            <TanStackTable
              data={rowData}
              columns={columns}
              onRowClick={handleRowClick}
              enablePagination={true}
              pagination={pagination}
              onPaginationChange={(newPage, newLimit) => {
                console.log('Pagination change requested:', newPage, newLimit);
                // Update state and fetch data with the new values
                setPagination(prev => ({...prev, page: newPage, limit: newLimit}));
                fetchCustomers({page: newPage, limit: newLimit});
              }}
              searchQuery={searchQuery}
              onSearchChange={(search) => {
                setSearchQuery(search);
                setPagination(prev => ({...prev, page: 1}));
                fetchCustomers({search, page: 1});
              }}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={(sortBy, sortOrder) => {
                setSortBy(sortBy);
                setSortOrder(sortOrder);
                fetchCustomers({sortBy, sortOrder});
              }}
            />
          )}
          {deleteMode && (
            <Box>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Select</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Whatsapp Number</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowData.map((customer) => (
                    <TableRow key={customer.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedCustomers.includes(customer.id)}
                          onChange={() => {
                            setSelectedCustomers((prev) =>
                              prev.includes(customer.id)
                                ? prev.filter((id) => id !== customer.id)
                                : [...prev, customer.id]
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell>{customer.id}</TableCell>
                      <TableCell>{customer.customer_name}</TableCell>
                      <TableCell>{customer.email}</TableCell>
                      <TableCell>{customer.contact_number}</TableCell>
                      <TableCell>{customer.meta?.whatsapp_number || ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default CustomersList;
